/**
 * Shows floating XP text for explicit non-combat reward events.
 *
 * XP totals received through SERVER_STATE_UPDATE are hydration snapshots, not
 * proof that XP was just earned. Deriving feedback from `player.xp` made every
 * room transfer that rebuilt the local character at zero replay the character's
 * entire current-level XP as a new gain. Combat XP already has a single visual
 * owner in KillBeatDirector/MobRenderer, so this hook intentionally listens only
 * to reward messages that otherwise have no world-space presentation owner.
 */

import { useEffect, useRef } from 'react';
import { Helix2000Character } from '../../shared/CharacterTypes';
import { DevConfig } from '../../config/environment/devConfig';
import { EventBus, EVENTS } from '../../shared/EventBus';
import type { GameScene } from '../scenes/GameScene';
import { livePlayerPosition } from '../systems/player/livePlayerPosition';

interface UseXPFeedbackProps {
  player: Helix2000Character | null;
  /** Retained as the hook boundary; presentation itself is EventBus-routed. */
  gameWorld?: GameScene | null;
}

export const useXPFeedback = ({ player }: UseXPFeedbackProps) => {
  const playerRef = useRef(player);
  playerRef.current = player;

  useEffect(() => {
    const eventBus = EventBus.getInstance();
    return eventBus.on(EVENTS.NET_MESSAGE_QUEST_COMPLETED, (message) => {
      const currentPlayer = playerRef.current;
      const amount = message?.rewards?.xp;
      if (
        DevConfig.zenMode
        || !currentPlayer
        || typeof amount !== 'number'
        || !Number.isFinite(amount)
        || amount <= 0
      ) {
        return;
      }

      const at = livePlayerPosition(currentPlayer.position);
      eventBus.emit(EVENTS.SHOW_XP_GAIN, {
        amount,
        x: at.x,
        y: at.y - 50,
      });
    });
  }, []);
};

export default useXPFeedback;
